export enum SubscriptionBillingType {
  Free = 'Free',
  Fixed = 'Fixed',
  Commission = 'Commission',
  PerTrade = 'Per_trade',
}

/**
 * 📃 Base model representing an available subscription plan
 */
export default abstract class SubscriptionPlan {
  // 📦 Formatted cost of the subscription plan
  public displayCost!: string;

  constructor(
    public type: string,
    public description: string,
    public priority: number,
    public billingType: SubscriptionBillingType,
    public allowedBots: number,
    public maxTotalInUse: number,
    public minBalanceInUse: number,
    public priceIds: string[],
    public benefits: string[],
    public freePro: boolean,
    public paylink: string
  ) {}

  // 📦 Color that is used to decorate the plan's card
  abstract get decorationColor(): string;

  // 📦 Displayable value for the allowed bots of the subscription
  public get formattedAllowedBots(): string {
    return this.allowedBots.toString();
  }

  // 📦 Displayable value for the max total in use of the subscription
  public get formattedMaxTotalInUse(): string {
    return this.maxTotalInUse.toString();
  }

  // 🚩 Flag for whether the current plan is the "Free" plan
  public get isFree(): boolean {
    return this.billingType === SubscriptionBillingType.Free && !this.freePro;
  }

  // 🚩 Flag for whether the current plan is the Admin plan
  public get isAdmin(): boolean {
    return this.billingType === SubscriptionBillingType.Free && this.type === 'Admin';
  }

  // 🚩 Flag for whether the current plan is of "Commission" type
  public get isCommissionBased(): boolean {
    return this.billingType === SubscriptionBillingType.Commission;
  }

  // 🚩 Flag for whether the current plan is of "Fixed Price" type
  public get isFixedPriceBased(): boolean {
    return this.billingType === SubscriptionBillingType.Fixed;
  }

  // 🚩 Flag for whether the current plan is of "Fee per trade" type
  public get isFeePerTrade(): boolean {
    return this.billingType === SubscriptionBillingType.PerTrade;
  }

  // 🚩 Flag for whether the current plan is of "Lifetime" type
  public get isLifetimePlan(): boolean {
    return this.billingType === SubscriptionBillingType.Free && this.type.includes('Lifetime');
  }

  // 🚩 Flag for whether the current plan is of "Lifetime Plus" type
  public get isLifetimePlusPlan(): boolean {
    return (
      this.billingType === SubscriptionBillingType.Free && this.type.includes('Lifetime Plus')
    );
  }

  public get cryptoPaymentCheckout(): string {
    if (this.type.includes('Traveler')) {
      return 'https://commerce.coinbase.com/checkout/b9983484-c77d-4548-aafc-3e3614b44eed';
    }

    if (this.type.includes('Venturer')) {
      return 'https://commerce.coinbase.com/checkout/33d51a1d-6a7c-444f-8eef-eff03b40b226';
    }

    if (this.type.includes('Voyager')) {
      return 'https://commerce.coinbase.com/checkout/08096dc7-1e02-4386-b344-8935b555301b';
    }
    return '';
  }
}

/**
 * 📄 Model representing the free plan on the platform
 */
export class FreeSubscriptionPlan extends SubscriptionPlan {
  constructor(
    type: string,
    description: string,
    priority: number,
    billingType: SubscriptionBillingType,
    allowedBots: number,
    maxTotalInUse: number,
    minBalanceInUse: number,
    priceIds: string[],
    benefits: string[],
    freePro: boolean
  ) {
    super(
      type,
      description,
      priority,
      billingType,
      allowedBots,
      maxTotalInUse,
      minBalanceInUse,
      priceIds,
      benefits,
      freePro
    );
    this.displayCost = '$0/month';
  }

  public get decorationColor(): string {
    return 'grey lighten-2';
  }
}

/**
 * 📄 Model representing a commission-based subscription plan
 */
export class CommisionSubscriptionPlan extends SubscriptionPlan {
  constructor(
    type: string,
    description: string,
    priority: number,
    billingType: SubscriptionBillingType,
    allowedBots: number,
    maxTotalInUse: number,
    minBalanceInUse: number,
    priceIds: string[],
    benefits: string[],
    freePro: boolean,
    public commissionPercentage: number
  ) {
    super(
      type,
      description,
      priority,
      billingType,
      allowedBots,
      maxTotalInUse,
      minBalanceInUse,
      priceIds,
      benefits,
      freePro
    );
    this.displayCost = '$0/month + ' + this.commissionPercentage * 100 + '% of bot profits/month';
  }

  public get decorationColor(): string {
    return 'primary';
  }
}

/**
 * 📄 Model representing a fixed price subscription plan
 */
export class FixedPriceSubscriptionPlan extends SubscriptionPlan {
  constructor(
    type: string,
    description: string,
    priority: number,
    billingType: SubscriptionBillingType,
    allowedBots: number,
    maxTotalInUse: number,
    minBalanceInUse: number,
    priceIds: string[],
    benefits: string[],
    freePro: boolean,
    public pricePerMonth: number,
    public obtPrice: number,
    public paylink: string
  ) {
    super(
      type,
      description,
      priority,
      billingType,
      allowedBots,
      maxTotalInUse,
      minBalanceInUse,
      priceIds,
      benefits,
      freePro
    );
    this.displayCost = '$' + this.pricePerMonth + '/month';
  }

  public get decorationColor(): string {
    switch (this.type) {
      case '✈️ Traveler':
        return 'light-blue lighten-3';
      case '🛰️ Venturer':
        return 'cyan';
      default:
        return 'accent';
    }
  }
}

/**
 * 📄 Model representing a fee per trade subscription plan
 */
export class FeePerTradeSubscriptionPlan extends SubscriptionPlan {
  constructor(
    type: string,
    description: string,
    priority: number,
    billingType: SubscriptionBillingType,
    allowedBots: number,
    maxTotalInUse: number,
    minBalanceInUse: number,
    priceIds: string[],
    benefits: string[],
    freePro: boolean,
    public obtFeePerTradePercentage: number,
    public adjustedPctPerTrade: number
  ) {
    super(
      type,
      description,
      priority,
      billingType,
      allowedBots,
      maxTotalInUse,
      minBalanceInUse,
      priceIds,
      benefits,
      freePro
    );
    this.displayCost = (adjustedPctPerTrade * 100).toFixed(3) + '% of traded volume per trade';
  }

  public get decorationColor(): string {
    return 'primary';
  }
}

/**
 * 📄 Model representing the lifetime plan on the platform
 */
export class LifetimeSubscriptionPlan extends SubscriptionPlan {
  constructor(
    type: string,
    description: string,
    priority: number,
    billingType: SubscriptionBillingType,
    allowedBots: number,
    maxTotalInUse: number,
    minBalanceInUse: number,
    priceIds: string[],
    benefits: string[],
    freePro: boolean
  ) {
    super(
      type,
      description,
      priority,
      billingType,
      allowedBots,
      maxTotalInUse,
      minBalanceInUse,
      priceIds,
      benefits,
      freePro
    );
    this.displayCost = 'Lifetime prepaid';
  }

  public get decorationColor(): string {
    return 'teal darken-1';
  }

  public get formattedAllowedBots(): string {
    return '∞';
  }

  public get formattedMaxTotalInUse(): string {
    return '∞';
  }
}

/**
 * 🔨 Function to map a certain raw object to the appropriate subscription plan
 * @param rawPlan
 * @param priority
 * @returns
 */
export function mapSubscriptionPlan(rawPlan: any, priority = 0): SubscriptionPlan {
  switch (rawPlan.billingType) {
    case SubscriptionBillingType.Fixed: {
      return new FixedPriceSubscriptionPlan(
        rawPlan.sub,
        rawPlan.description,
        priority * -1,
        rawPlan.billingType,
        rawPlan.allowedBots,
        rawPlan.maxTotalInUse,
        rawPlan.minBalanceUse,
        rawPlan.priceIds,
        rawPlan.benefits,
        rawPlan.freePro,
        rawPlan.pricePerMonth,
        rawPlan.obtPrice,
        rawPlan.paylink
      );
    }
    case SubscriptionBillingType.Commission: {
      return new CommisionSubscriptionPlan(
        rawPlan.sub,
        rawPlan.description,
        priority * -1,
        rawPlan.billingType,
        rawPlan.allowedBots,
        rawPlan.maxTotalInUse,
        rawPlan.minBalanceUse,
        rawPlan.priceIds,
        rawPlan.benefits,
        rawPlan.freePro,
        rawPlan.commissionPercentage
      );
    }
    case SubscriptionBillingType.PerTrade: {
      return new FeePerTradeSubscriptionPlan(
        rawPlan.sub,
        rawPlan.description,
        priority * -1,
        rawPlan.billingType,
        rawPlan.allowedBots,
        rawPlan.maxTotalInUse,
        rawPlan.minBalanceUse,
        rawPlan.priceIds,
        rawPlan.benefits,
        rawPlan.freePro,
        rawPlan.pctPerTrade,
        rawPlan.adjustedPctPerTrade
      );
    }
    default: {
      if (rawPlan.sub.includes('Lifetime')) {
        return new LifetimeSubscriptionPlan(
          rawPlan.sub,
          rawPlan.description,
          priority * -1,
          rawPlan.billingType,
          rawPlan.allowedBots,
          rawPlan.maxTotalInUse,
          rawPlan.minBalanceUse,
          rawPlan.priceIds,
          rawPlan.benefits,
          rawPlan.freePro
        );
      }
      return new FreeSubscriptionPlan(
        rawPlan.sub,
        rawPlan.description,
        priority * -1,
        rawPlan.billingType,
        rawPlan.allowedBots,
        rawPlan.maxTotalInUse,
        rawPlan.minBalanceUse,
        rawPlan.priceIds,
        rawPlan.benefits,
        rawPlan.freePro
      );
    }
  }
}

// 🤡 Data using for testing
export const subscriptionPlanExamples: SubscriptionPlan[] = [
  new FreeSubscriptionPlan(
    'Free Plan',
    "The beginner's playground",
    0,
    SubscriptionBillingType.Free,
    1,
    500,
    50,
    ['price_1'],
    ['Limited strategies', '1 connected exchange', 'Basic customer support'],
    false
  ),
  new FixedPriceSubscriptionPlan(
    '🧪 Experimenter Plan',
    'Testing the water',
    1,
    SubscriptionBillingType.Fixed,
    3,
    1500,
    50,
    ['price_1'],
    ['All strategies', 'Unlimited connected exchanges', 'Priority customer support (24/7)'],
    false,
    7.99,
    10
  ),
  new CommisionSubscriptionPlan(
    '🚀 To The Moon Plan',
    "Let's get this money",
    2,
    SubscriptionBillingType.Commission,
    12,
    100000,
    50,
    ['price_1'],
    ['All strategies', 'Unlimited connected exchanges', 'Priority customer support (24/7)'],
    false,
    30
  ),
];
